import React, { useState, useEffect } from 'react';
import { Heading, Text } from '@/Components/ui/typography';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { useCompanyCredits } from './useCompanyCredits';
import { CreditUsageHistory } from './CreditUsageHistory';
import { BillingCycleToggle } from './BillingCycleToggle';
import { PricingPlansSection } from './PricingPlansSection';
import { reconcileCreditsByBlockedJobIds } from '../../utils/creditsReconcile';
import { getApiUrl } from '@/config/api';
import { Zap, CreditCard, CalendarDays, ArrowUpRight, Clock3, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCan } from '@/HireWithUs/Context/CompanyAccessContext';

// Marketing plan cards. Prices are per-month INR; credits are the monthly grant
// (1 credit ≈ ₹25). Quarterly/annual are derived in PricingPlansSection. Kept in
// sync with the entitlement plans (Pilot 7 / Growth 8 / Scale 9 / Enterprise 10).
const PLANS = [
  {
    id: 7,
    name: 'Pilot',
    idealFor: 'Solo recruiters or small teams getting started',
    monthlyPrice: 4999,
    credits: 200,
    features: ['AI Screening Interviews', 'Aptitude Tests', 'Resume Screening', 'AI Voice JD Creation', 'Cheating Detection'],
    isPopular: false,
  },
  {
    id: 8,
    name: 'Growth',
    idealFor: 'HR teams hiring a few candidates a month',
    monthlyPrice: 14999,
    credits: 600,
    features: ['Everything in Pilot', 'Realtime AI Interviews', 'Coding Tests', 'Live Meet Interviews', 'WhatsApp Messaging', 'Video Resume', 'Offer Letter Generation'],
    isPopular: true,
  },
  {
    id: 9,
    name: 'Scale',
    idealFor: 'Teams hiring at higher volume',
    monthlyPrice: 34999,
    credits: 1400,
    features: ['Everything in Growth', 'AI Phone Screening', 'Auto-screen on Apply', 'Market Trends Intel', 'Premium College-Tier Filter', 'Identity + Liveness Verification', 'Dedicated Account Manager'],
    isPopular: false,
  },
  {
    id: 10,
    name: 'Enterprise',
    idealFor: 'Large organizations hiring at scale',
    monthlyPrice: 0,
    credits: 0,
    features: ['Everything in Scale', 'API / ATS Integration', 'Custom AI Test Combinations', 'Multiple Tests in One Flow', 'Custom Workflows & SLA', 'Priority Support'],
    isPopular: false,
  },
];

export function PlansBillingPage() {
  const navigate = useNavigate();
  const canManageBilling = useCan('billing.manage');
  const { loading, summary } = useCompanyCredits();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [pendingReq, setPendingReq] = useState(null);       // active plan-change request
  const [cancelAtEnd, setCancelAtEnd] = useState(false);    // cancellation scheduled
  const [planExpiresAt, setPlanExpiresAt] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [quotas, setQuotas] = useState(null);   // {active_job_posts:{limit,used,remaining}, recruiter_seats:{...}}
  // Custom monthly allowance (e.g. Balio): {items:[{key,label,limit,used,remaining}], renews_on, support_email}
  const [monthlyLimits, setMonthlyLimits] = useState(null);

  useEffect(() => {
    const cid = localStorage.getItem('companyAccountId');
    if (!cid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/entitlements/me?company_id=${encodeURIComponent(cid)}`), { credentials: 'include' });
        const data = await res.json();
        if (!cancelled) {
          setQuotas(data?.quotas || null);
          setMonthlyLimits(data?.monthly_limits?.items?.length ? data.monthly_limits : null);
        }
      } catch { /* fail-soft */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem('companyAccountId') || localStorage.getItem('companyId') || localStorage.getItem('companyID');
    if (!companyId) return;
    reconcileCreditsByBlockedJobIds({ companyId, getApiUrl }).catch(() => {});
  }, []);

  const loadRequest = async () => {
    try {
      const res = await fetch(getApiUrl('/api/plan-requests/mine'), { credentials: 'include' });
      const data = await res.json();
      setPendingReq(data?.request || null);
      setCancelAtEnd(Boolean(data?.cancel_at_period_end));
      setPlanExpiresAt(data?.plan_expires_at || null);
    } catch { /* fail-soft */ }
  };
  useEffect(() => { loadRequest(); }, []);

  const submitRequest = async (bodyObj, successMsg) => {
    setActionBusy(true);
    try {
      const res = await fetch(getApiUrl('/api/plan-requests'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(bodyObj),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed.');
      toast.success(successMsg);
      await loadRequest();
    } catch (e) {
      toast.error(e.message || 'Could not submit the request.');
    } finally { setActionBusy(false); }
  };

  const handleRequestPlan = (plan) => {
    if (!plan?.id) { navigate('/hirewithus/Connect'); return; }
    submitRequest({ requested_plan_id: plan.id },
      `Request to switch to ${plan.name} submitted — pending approval.`);
  };

  const handleCancelPlan = () => {
    if (!window.confirm('Request cancellation of your plan? It stays active until the end of your current cycle.')) return;
    submitRequest({ request_type: 'cancel' }, 'Cancellation requested — your plan runs until the cycle end.');
  };

  const handleWithdraw = async () => {
    setActionBusy(true);
    try {
      const res = await fetch(getApiUrl('/api/plan-requests/mine/withdraw'), { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('Could not withdraw the request.');
      toast.success('Request withdrawn.');
      await loadRequest();
    } catch (e) {
      toast.error(e.message || 'Could not withdraw.');
    } finally { setActionBusy(false); }
  };

  const handleContactSales = () => {
    navigate('/hirewithus/Connect');
  };

  const available  = summary.credits_available ?? summary.credit_balance ?? 0;
  const total      = summary.credits_total ?? 0;
  const used       = summary.credits_used ?? 0;
  const blocked    = summary.credits_blocked ?? 0;
  const planName   = summary.plan_name || 'No active plan';
  // Custom contract (e.g. Custom Enterprise): no self-serve upgrade offers.
  const isCustomPlan = Boolean(summary.is_custom_plan);
  const usedPct    = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const availPct   = total > 0 ? Math.min(100, Math.round((available / total) * 100)) : 0;

  const creditBarColor = availPct > 50 ? 'bg-green-500' : availPct > 20 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="flex flex-col w-full gap-6">

      {/* ── Page header ── */}
      <div>
        <Heading level="h1" className="text-2xl sm:text-3xl font-bold text-foreground m-0">
          Plans & Billing
        </Heading>
        <Text size="sm" className="text-muted-foreground mt-1 m-0">
          Manage your credits, view usage history and upgrade your plan.
        </Text>
      </div>

      {/* ── Top summary row: 2 compact stat cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Available credits */}
        <Card className="border border-blue-200 bg-blue-50/50 shadow-sm">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="shrink-0 rounded-lg bg-blue-100 p-2">
              <CreditCard className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <Text size="xs" className="text-muted-foreground font-medium m-0">Available credits</Text>
              {loading ? <Skeleton className="mt-1 h-7 w-20" /> : (
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <Heading level="h2" className="text-2xl font-bold text-blue-600 m-0 tabular-nums">{available}</Heading>
                  {total > 0 && (
                    <Text size="xs" className="text-muted-foreground m-0">{available} of {total} remaining</Text>
                  )}
                </div>
              )}
              {!loading && total > 0 && (
                <div className="mt-2 h-1 w-full rounded-full bg-blue-100 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${creditBarColor}`} style={{ width: `${availPct}%` }} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Current plan */}
        <Card className="border border-violet-200 bg-violet-50/50 shadow-sm">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="shrink-0 rounded-lg bg-violet-100 p-2">
              <CalendarDays className="w-4 h-4 text-violet-600" />
            </div>
            <div className="min-w-0 flex-1">
              <Text size="xs" className="text-muted-foreground font-medium m-0">Current plan</Text>
              {loading ? <Skeleton className="mt-1 h-7 w-28" /> : (
                <div className="flex flex-wrap items-center gap-2">
                  <Heading level="h2" className="text-lg font-bold text-violet-600 m-0">{planName}</Heading>
                  {planName !== 'No active plan' && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 border-violet-300 text-violet-600">Active</Badge>
                  )}
                </div>
              )}
              {!loading && (
                <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <Text size="xs" className="text-muted-foreground m-0">
                    {isCustomPlan && summary.custom_plan_auto_renew && !cancelAtEnd
                      ? 'Tailored for your team · renews automatically every month'
                      : summary.renewal_date
                        ? `Renews ${new Date(summary.renewal_date).toLocaleDateString()}`
                        : (summary.plan_validity_days ? `${summary.plan_validity_days}-day cycle` : 'No expiry')}
                  </Text>
                  {canManageBilling && planName !== 'No active plan' && !cancelAtEnd
                    && !(pendingReq && pendingReq.request_type === 'cancel') && (
                    <button
                      type="button"
                      onClick={handleCancelPlan}
                      disabled={actionBusy}
                      className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                    >
                      Cancel plan
                    </button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* ── Usage: monthly allowance (custom contracts) + plan quotas, in one compact card ── */}
      {(monthlyLimits || (quotas && (quotas.active_job_posts || quotas.recruiter_seats))) && (
        <Card className="border border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5 space-y-4">
            {monthlyLimits && (
              <>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    <Heading level="h3" className="text-base font-semibold text-slate-800 m-0">This month&apos;s allowance</Heading>
                    <Text size="xs" className="text-muted-foreground m-0 mt-0.5">
                      Renews on {monthlyLimits.renews_on}
                      {monthlyLimits.monthly_credits != null && (
                        <> · {Number(monthlyLimits.monthly_credits).toLocaleString()} credits every month (unused credits don&apos;t carry over)</>
                      )}
                    </Text>
                  </div>
                  <Text size="xs" className="text-muted-foreground m-0 shrink-0">
                    Need more?{' '}
                    <a href={`mailto:${monthlyLimits.support_email}`} className="font-medium text-blue-600 hover:underline">{monthlyLimits.support_email}</a>
                  </Text>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3.5">
                  {monthlyLimits.items.map((q) => {
                    const pct = q.limit > 0 ? Math.min(100, Math.round((q.used / q.limit) * 100)) : 0;
                    const out = q.remaining === 0;
                    const barColor = out ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500';
                    return (
                      <div key={q.key} className="min-w-0">
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <span className="min-w-0 text-slate-600 first-letter:uppercase">{q.label}</span>
                          <span className="flex shrink-0 items-center gap-2 tabular-nums">
                            <span>
                              <span className="font-semibold text-slate-800">{q.used}</span>
                              <span className="text-slate-400"> / {q.limit}</span>
                            </span>
                            {out
                              ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">Limit reached</span>
                              : <span className="text-xs text-muted-foreground">{q.remaining} left</span>}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {quotas && (quotas.active_job_posts || quotas.recruiter_seats) && (
              <div className={`flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground ${monthlyLimits ? 'border-t border-slate-100 pt-3' : ''}`}>
                {[
                  { key: 'active_job_posts', label: 'Active job posts' },
                  { key: 'recruiter_seats', label: 'Recruiter seats' },
                ].map(({ key, label }) => {
                  const q = quotas[key];
                  if (!q) return null;
                  const unlimited = q.limit == null;
                  const full = !unlimited && q.remaining === 0;
                  return (
                    <span key={key}>
                      {label}:{' '}
                      <span className="font-semibold text-slate-700 tabular-nums">{q.used}{unlimited ? '' : ` / ${q.limit}`}</span>
                      {' '}
                      <span className={full ? 'text-red-600 font-medium' : ''}>
                        ({unlimited ? 'unlimited' : full ? 'limit reached' : `${q.remaining} left`})
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Plan lifecycle status: pending request / scheduled cancellation ── */}
      {pendingReq && (
        <Card className="border border-amber-200 bg-amber-50/60 shadow-sm">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2">
              <Clock3 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <Text size="sm" className="text-amber-900 m-0">
                {pendingReq.request_type === 'cancel'
                  ? 'Cancellation requested — pending admin approval.'
                  : `Request to switch to ${pendingReq.requested_plan_name || 'a new plan'} — pending admin approval.`}
              </Text>
            </div>
            {canManageBilling && <Button size="sm" variant="outline" disabled={actionBusy} onClick={handleWithdraw} className="shrink-0">Withdraw request</Button>}
          </CardContent>
        </Card>
      )}
      {!pendingReq && cancelAtEnd && (
        <Card className="border border-red-200 bg-red-50/60 shadow-sm">
          <CardContent className="p-4 flex items-start gap-2">
            <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <Text size="sm" className="text-red-900 m-0">
              Your plan is set to cancel{planExpiresAt ? ` on ${new Date(planExpiresAt).toLocaleDateString()}` : ' at the end of the current cycle'}. You keep access and remaining credits until then.
            </Text>
          </CardContent>
        </Card>
      )}

      {/* ── Upgrade CTA banner ── */}
      {!isCustomPlan && canManageBilling && (
      <Card className="border border-primary/20 bg-primary/5 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Heading level="h3" className="text-base font-semibold text-foreground m-0">
                Need more credits?
              </Heading>
              <Text size="sm" className="text-muted-foreground mt-0.5 m-0">
                Upgrade your plan for more credits and advanced features. No hidden fees.
              </Text>
            </div>
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => document.getElementById('upgrade-plans')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              View Plans
              <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* ── Credit usage history ── */}
      <CreditUsageHistory />

      {/* ── Upgrade plans ── */}
      {!isCustomPlan && canManageBilling && (
      <div id="upgrade-plans" className="scroll-mt-4 space-y-4">
        <div>
          <Heading level="h2" className="text-xl font-semibold text-foreground m-0">
            Upgrade options
          </Heading>
          <Text size="sm" className="text-muted-foreground mt-1 m-0">
            Choose a plan that fits your hiring needs. Credits increase with your new plan.
          </Text>
        </div>

        <div className="flex justify-center">
          <BillingCycleToggle value={billingCycle} onValueChange={setBillingCycle} />
        </div>

        <PricingPlansSection
          plans={PLANS}
          billingCycle={billingCycle}
          currentPlanName={planName}
          pendingRequest={pendingReq}
          actionBusy={actionBusy}
          onRequest={handleRequestPlan}
          onContactSales={handleContactSales}
        />
      </div>
      )}

    </div>
  );
}
